import React, { useCallback, useMemo } from 'react';

import {
	CONNECTOR_OAUTH_STATUS,
	type ConnectorOAuthStatus,
} from '@confluence/connectors/entry-points/connector-oauth-types.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { readConnectorRowsFromLocalStorage } from './connectors/readConnectorRowsFromLocalStorage.ts';
import type { QualifyingProduct } from './thirdPartyPromptTypes.ts';
import { ThirdPartyRelatedContentPromoCard } from './ThirdPartyRelatedContentPromoCard.tsx';
import { ThirdPartyRelatedContentPromoCardSkeleton } from './ThirdPartyRelatedContentPromoCardSkeleton.tsx';
import { useThirdPartyConnectorOAuth } from './useThirdPartyConnectorOAuth.ts';

const promptIsGone = (status: ConnectorOAuthStatus, isPromptHidden: boolean): boolean =>
	isPromptHidden || status === CONNECTOR_OAUTH_STATUS.CONNECTED;

/**
 * Mounted once the product is known. `status` is the outcome, not promise settlement: `connect`
 * resolves on a cancelled popup too, so awaiting it would report success when the reader closed the
 * window. The promo card stays mounted after connect so its CONNECTING → CONNECTED flag can fire;
 * `children` is the dropped rec that fills the same cell.
 */
const ConnectedPromptCard = ({
	product,
	children,
}: {
	product: QualifyingProduct;
	children?: React.ReactNode;
}) => {
	const { cloudId, userId } = useSessionData();

	const connectorRows = useMemo(
		() => readConnectorRowsFromLocalStorage({ userId: userId ?? '', cloudId }).rows,
		[cloudId, userId],
	);

	const { status, connect, isPromptHidden } = useThirdPartyConnectorOAuth({
		appId: product.appId,
		connectorRows,
		cloudId,
		userId,
	});

	const startConnect = useCallback(() => {
		void connect();
	}, [connect]);

	return (
		<>
			<ThirdPartyRelatedContentPromoCard
				product={product}
				status={status}
				onConnect={startConnect}
				showCardAsListitem={false}
			/>
			{promptIsGone(status, isPromptHidden) ? children : null}
		</>
	);
};

/**
 * The leading related-content slot. `product` / `isResolved` come from
 * `useThirdPartyPromptPlacement` so this does not walk the document again. The grid only mounts
 * this while that prompt should occupy the first cell. After connect, `children` is the rec that
 * was dropped so the rest of the grid does not move.
 */
export const ThirdPartyPromptSlot = ({
	product,
	isResolved,
	children,
}: {
	product: QualifyingProduct | undefined;
	isResolved: boolean;
	children?: React.ReactNode;
}): React.JSX.Element | null => {
	if (!isResolved) {
		return <ThirdPartyRelatedContentPromoCardSkeleton />;
	}

	if (product) {
		return <ConnectedPromptCard product={product}>{children}</ConnectedPromptCard>;
	}

	return children ? <>{children}</> : null;
};
